/**
 * @fileoverview `FollowArtistButton` — live follow toggle for `/artist/[slug]`.
 *
 * Signed-in taps call `useFollowArtistMutation` / `useUnfollowArtistMutation`
 * (`@loci/api`) with an optimistic press state and rollback + error toast on
 * failure, matching `ListView` hearts and `SavedEventsFullView`. Followed
 * state hydrates from `useGetArtistFollowQuery` (`GET v1/artists/:id/follow`)
 * so a return visit matches the `UserFollow` graph even when the artist is
 * past the first page of `getFollowedArtists` (WEB-948).
 *
 * Anonymous taps are auth-gated: `crossHostLogin` with `force: true` and the
 * current city URL as `next` (absolute `window.location.href` when it is a
 * LOCI host — WEB-898 — else the same-origin path). They do not write
 * `localStorage`. Existing `loci.followedArtists` keys are not migrated
 * (WEB-849).
 *
 * @since WEB-100
 */
'use client';

import * as React from 'react';
import { Bell, BellOff, RefreshCw } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useNotification } from '@loci/store';
import {
  useFollowArtistMutation,
  useGetArtistFollowQuery,
  useUnfollowArtistMutation,
} from '@loci/api';
import { useAuth } from '@/contexts/AuthContext';
import { crossHostLogin, getValidCrossHostRedirect } from '@/lib/cross-host-redirect';
import { cn } from '@/components/atoms/primitives/utils';

const PILL_CLASS =
  'inline-flex min-h-11 desktop:min-h-0 w-full items-center justify-center gap-2 rounded-full border px-4 py-3 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-70 disabled:pointer-events-none';

export interface FollowArtistButtonViewProps {
  followed: boolean;
  pending?: boolean;
  onToggle: () => void;
  className?: string;
  /** Query failed with no follow value — show retry instead of Follow. */
  loadError?: boolean;
  onRetry?: () => void;
}

export interface FollowArtistButtonProps {
  artistId: string;
  artistName: string;
  className?: string;
}

interface OptimisticFollowState {
  artistId: string;
  following: boolean;
}

export function FollowArtistButtonView({
  followed,
  pending = false,
  onToggle,
  className,
  loadError = false,
  onRetry,
}: FollowArtistButtonViewProps): React.ReactElement {
  if (loadError) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <p role="alert" className="text-center text-sm text-muted-foreground">
          Couldn&apos;t load follow status.
        </p>
        <button
          type="button"
          onClick={onRetry}
          disabled={pending || onRetry === undefined}
          aria-busy={pending || undefined}
          className={cn(PILL_CLASS, 'border-border bg-card text-foreground hover:bg-muted')}
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Try again
        </button>
      </div>
    );
  }

  const Icon = followed ? Bell : BellOff;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={pending}
      aria-pressed={followed}
      aria-busy={pending || undefined}
      className={cn(
        PILL_CLASS,
        followed
          ? 'border-foreground bg-foreground text-background hover:opacity-90'
          : 'border-border bg-card text-foreground hover:bg-muted',
        className,
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {followed ? 'Following' : 'Follow'}
    </button>
  );
}

function currentUrlForAuthReturn(): string {
  if (typeof window === 'undefined') return '/';
  const href = window.location.href;
  const validated = getValidCrossHostRedirect(href, '__INVALID__');
  if (validated !== '__INVALID__') return validated;
  return `${window.location.pathname}${window.location.search}`;
}

export function FollowArtistButton({
  artistId,
  artistName,
  className,
}: FollowArtistButtonProps): React.ReactElement {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { showNotification } = useNotification();
  const [followArtist] = useFollowArtistMutation();
  const [unfollowArtist] = useUnfollowArtistMutation();
  const { currentData, isFetching, isError, refetch } = useGetArtistFollowQuery(
    isAuthenticated ? { artistId } : skipToken,
  );

  const serverFollowed = isAuthenticated ? (currentData?.following ?? false) : false;
  const [optimistic, setOptimistic] = React.useState<OptimisticFollowState | null>(null);
  const [pendingArtistId, setPendingArtistId] = React.useState<string | null>(null);
  const noCacheFollowErrorArtistIdRef = React.useRef<string | null>(null);

  const optimisticForArtist =
    optimistic !== null && optimistic.artistId === artistId ? optimistic.following : null;
  const pending = pendingArtistId === artistId;

  React.useEffect(() => {
    if (optimisticForArtist === null) return;
    if (optimisticForArtist === serverFollowed) setOptimistic(null);
  }, [optimisticForArtist, serverFollowed]);

  const followed = optimisticForArtist ?? serverFollowed;
  const hydrating = Boolean(
    isAuthenticated && isFetching && currentData === undefined && optimisticForArtist === null,
  );
  // RTK Query clears isError as soon as a refetch is pending. Remember the
  // no-cache error per artist so retry stays on the error view until this
  // artist's query returns data or settles without error.
  if (isAuthenticated && isError && currentData === undefined && optimisticForArtist === null) {
    noCacheFollowErrorArtistIdRef.current = artistId;
  } else if (
    noCacheFollowErrorArtistIdRef.current === artistId &&
    (!isAuthenticated || currentData !== undefined || (!isFetching && !isError))
  ) {
    noCacheFollowErrorArtistIdRef.current = null;
  }
  // Query failed with no cached value: do not default to Follow. The view
  // shows a retry instead, and handleToggle still no-ops while unknown.
  const followStateUnknown = Boolean(
    isAuthenticated &&
    currentData === undefined &&
    optimisticForArtist === null &&
    (isError || (isFetching && noCacheFollowErrorArtistIdRef.current === artistId)),
  );

  const handleToggle = React.useCallback((): void => {
    if (authLoading || pending || hydrating || followStateUnknown) return;

    if (!isAuthenticated) {
      window.location.assign(crossHostLogin(currentUrlForAuthReturn(), { force: true }));
      return;
    }

    const next = !followed;
    setOptimistic({ artistId, following: next });
    setPendingArtistId(artistId);

    const mutate = next ? followArtist({ artistId }) : unfollowArtist({ artistId });
    void mutate
      .unwrap()
      .catch(() => {
        setOptimistic((current) =>
          current?.artistId === artistId && current.following === next
            ? { artistId, following: !next }
            : current,
        );
        showNotification({
          type: 'error',
          title: next ? `Failed to follow ${artistName}` : `Failed to unfollow ${artistName}`,
        });
      })
      .finally(() => {
        setPendingArtistId((current) => (current === artistId ? null : current));
      });
  }, [
    authLoading,
    pending,
    hydrating,
    followStateUnknown,
    isAuthenticated,
    followed,
    followArtist,
    unfollowArtist,
    artistId,
    artistName,
    showNotification,
  ]);

  const handleRetry = React.useCallback((): void => {
    if (!followStateUnknown) return;
    void refetch();
  }, [followStateUnknown, refetch]);

  return (
    <FollowArtistButtonView
      followed={followed}
      pending={followStateUnknown ? isFetching || authLoading : pending || hydrating || authLoading}
      onToggle={handleToggle}
      loadError={followStateUnknown}
      onRetry={handleRetry}
      className={className}
    />
  );
}
